/**
 * Finance > **Passageiros em débito** and **Bloqueio de dinheiro** (D536 AN-RIDER-DEBTS; D468 / ADR-0086) - the pure view-models behind the two screens over
 * `GET /v1/analytics/rider-debts/*` and `GET /v1/admin/rider-cash-blocks`.
 *
 * What the screens show, to whom (owner decisions, enforced by the SERVER; the console only presents and never asks for more than the grants allow):
 *  - every reader with `analytics:view` (READ_ONLY too) sees INDICATORS only: the open balance, what was created and recovered, the recovery rate, the age buckets, the count of riders
 *    blocked for cash and the charts - never a rider, a ride or a name;
 *  - the per-rider list, one rider's history, the CSV of riders and the blocked-for-cash screen need `analytics:view_personal` (and are access-logged); without it they are not drawn at all;
 *  - the release of a block is four eyes with the EXISTING routes: `rider_cash:propose` (the maker) and `rider_cash:approve` (the checker, never the proposer: the console hides the
 *    decision on one's own proposal and says why, the server refuses it anyway).
 *
 * Money is an integer-minor-unit string next to an ISO currency: parsed to bigint, never to a float for a figure (the charts take numbers for geometry only); a currency is never summed
 * with another; a malformed amount reads as unknown, never as zero. Neutral: markets, currencies and bucket ids are data.
 */
import type {
  RiderCashBlockView,
  RiderCashBlocksView,
  RiderCashReleaseView,
  RiderDebtBlockedFilter,
  RiderDebtBucket,
  RiderDebtCurrencySummary,
  RiderDebtListParams,
  RiderDebtOrigin,
  RiderDebtRiderRow,
  RiderDebtSort,
  RiderDebtStatus,
  RiderDebtSummaryView,
} from '@vello/api-client';
import { BI_EXPORT_POLICY, BI_PERSONAL_POLICY, grantsAllow, type KnownGrants } from './console-access.js';

// ---- authority (display only - the server re-authorizes every call) ----------------------------------------------

export function riderDebtAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayPersonal: boolean; readonly mayExport: boolean } {
  if (grants === undefined) return { mayView: true, mayPersonal: true, mayExport: true };
  return {
    mayView: grantsAllow(grants, { permission: 'analytics:view', target: 'GEO' }),
    mayPersonal: grantsAllow(grants, BI_PERSONAL_POLICY),
    mayExport: grantsAllow(grants, BI_EXPORT_POLICY),
  };
}

export function cashBlockAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayPropose: boolean; readonly mayApprove: boolean } {
  if (grants === undefined) return { mayView: true, mayPropose: true, mayApprove: true };
  return {
    // The list names people: both the market word and the personal word (the server asks for both).
    mayView: grantsAllow(grants, { permission: 'rider_cash:view', target: 'GEO' }) && grantsAllow(grants, BI_PERSONAL_POLICY),
    mayPropose: grantsAllow(grants, { permission: 'rider_cash:propose', target: 'GEO' }),
    mayApprove: grantsAllow(grants, { permission: 'rider_cash:approve', target: 'GEO' }),
  };
}

// ---- vocabulary -----------------------------------------------------------------------------------------------------

export const RIDER_DEBT_ORIGIN_LIST: readonly RiderDebtOrigin[] = ['CASH_SHORTFALL', 'CANCELLATION_FEE', 'OTHER'];
export const RIDER_DEBT_STATUS_LIST: readonly RiderDebtStatus[] = ['OPEN', 'PARTIAL', 'SETTLED'];
/** Youngest first. */
export const RIDER_DEBT_BUCKET_LIST: readonly RiderDebtBucket[] = ['D0_7', 'D8_30', 'D31_60', 'D61_PLUS'];
export const RIDER_DEBT_SORT_LIST: readonly RiderDebtSort[] = ['open_desc', 'age_desc', 'recent_payment_desc'];
export const RIDER_DEBT_BLOCKED_LIST: readonly RiderDebtBlockedFilter[] = ['any', 'yes', 'no'];

/** The reasons an operator can give for releasing a block (the server takes any stable code; the console offers a closed list). */
export const CASH_RELEASE_REASONS = ['DRIVER_MARKED_BY_MISTAKE', 'PAID_OUTSIDE_THE_APP', 'DEBT_DISPUTE_UPHELD', 'OPERATOR_GOODWILL', 'OTHER'] as const;
export type CashReleaseReason = (typeof CASH_RELEASE_REASONS)[number];

const MINOR = /^\d+$/;
export const minorOf = (raw: string | null | undefined): bigint | undefined => (typeof raw === 'string' && MINOR.test(raw) ? BigInt(raw) : undefined);

// ---- periods --------------------------------------------------------------------------------------------------------

export const RIDER_DEBT_PERIODS = ['this_month', 'last_month', 'last_30', 'last_90', 'this_year'] as const;
export type RiderDebtPeriod = (typeof RIDER_DEBT_PERIODS)[number];

const DAY = 86_400_000;
const addDays = (date: string, n: number): string => new Date(Date.parse(`${date}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10);

/** The window (market-local dates the store keeps) a named period stands for, as of `today` (the operator's date). */
export function riderDebtWindow(period: RiderDebtPeriod, today: string): { readonly from: string; readonly to: string } {
  const monthStart = `${today.slice(0, 7)}-01`;
  switch (period) {
    case 'this_month':
      return { from: monthStart, to: today };
    case 'last_month': {
      const to = addDays(monthStart, -1);
      return { from: `${to.slice(0, 7)}-01`, to };
    }
    case 'last_30':
      return { from: addDays(today, -29), to: today };
    case 'last_90':
      return { from: addDays(today, -89), to: today };
    case 'this_year':
      return { from: `${today.slice(0, 4)}-01-01`, to: today };
  }
}

// ---- the filters ----------------------------------------------------------------------------------------------------

export interface RiderDebtFilters {
  readonly period: RiderDebtPeriod;
  /** Empty: every market of the operator's reach (or `markets`, when the scope is a state). */
  readonly market: string;
  /** ADR-0124: the markets of the scoped STATE, used while `market` is empty (the route takes a market list, no region). Never empty - a state with no market sends one no market has. */
  readonly markets?: readonly string[];
  /** Empty: every origin. */
  readonly origins: readonly RiderDebtOrigin[];
  /** The riders IN debt by default: open and part-paid debts. */
  readonly statuses: readonly RiderDebtStatus[];
  readonly aging: readonly RiderDebtBucket[];
  readonly blocked: RiderDebtBlockedFilter;
  readonly sort: RiderDebtSort;
}

export const DEFAULT_RIDER_DEBT_FILTERS: RiderDebtFilters = { period: 'this_month', market: '', origins: [], statuses: ['OPEN', 'PARTIAL'], aging: [], blocked: 'any', sort: 'open_desc' };

const sameSet = <T extends string>(a: readonly T[], b: readonly T[]): boolean => a.length === b.length && a.every((x) => b.includes(x));

/** Toggle one value of a multi-select. */
export function toggledDebtValue<T extends string>(list: readonly T[], value: T): readonly T[] {
  return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
}

/** The market list a request carries: the one market, else the scoped state's markets, else none (every market of the reach). */
function marketsParam(f: RiderDebtFilters): { markets?: string[] } {
  if (f.market !== '') return { markets: [f.market] };
  return f.markets !== undefined && f.markets.length > 0 ? { markets: [...f.markets] } : {};
}

/** The filters pinned to the header scope: the scoped city (or empty) and, for a state, its market list; any other scope carries no list. */
export function scopedDebtFilters(f: RiderDebtFilters, market: string, stateMarkets: readonly string[] | undefined): RiderDebtFilters {
  return { period: f.period, market, ...(stateMarkets !== undefined ? { markets: stateMarkets } : {}), origins: f.origins, statuses: f.statuses, aging: f.aging, blocked: f.blocked, sort: f.sort };
}

/** The summary request: the period, the market and the origins (the status, age and blocked filters narrow the LIST, not the market-level figures). */
export function riderDebtSummaryParams(f: RiderDebtFilters, today: string): { from: string; to: string; markets?: string[]; origins?: RiderDebtOrigin[] } {
  const w = riderDebtWindow(f.period, today);
  return { from: w.from, to: w.to, ...marketsParam(f), ...(f.origins.length > 0 ? { origins: [...f.origins] } : {}) };
}

/** The list request. The period is the window the DEBTS arose in only when the operator narrows by it (`byPeriod`); otherwise every debt in debt now. */
export function riderDebtListParams(f: RiderDebtFilters, today: string, page: { readonly limit: number; readonly offset: number }, byPeriod: boolean): RiderDebtListParams {
  const w = riderDebtWindow(f.period, today);
  return {
    ...(byPeriod ? { from: w.from, to: w.to } : {}),
    ...marketsParam(f),
    ...(f.origins.length > 0 ? { origins: [...f.origins] } : {}),
    statuses: [...f.statuses],
    ...(f.aging.length > 0 ? { aging: [...f.aging] } : {}),
    blocked: f.blocked,
    sort: f.sort,
    limit: page.limit,
    offset: page.offset,
  };
}

/** How many filters differ from the default (the badge on the filter bar). */
export function activeFilterCount(f: RiderDebtFilters, byPeriod: boolean): number {
  const d = DEFAULT_RIDER_DEBT_FILTERS;
  return (f.market !== '' ? 1 : 0) + (f.origins.length > 0 ? 1 : 0) + (sameSet(f.statuses, d.statuses) ? 0 : 1) + (f.aging.length > 0 ? 1 : 0) + (f.blocked !== 'any' ? 1 : 0) + (byPeriod ? 1 : 0) + (f.sort !== d.sort ? 1 : 0);
}

/** A bucket narrows OPEN debts only: asking for one removes SETTLED from the statuses (the server does the same, and the screen says so). */
export function withAging(f: RiderDebtFilters, bucket: RiderDebtBucket): RiderDebtFilters {
  const aging = toggledDebtValue(f.aging, bucket);
  return { ...f, aging, statuses: aging.length > 0 ? f.statuses.filter((s) => s !== 'SETTLED') : f.statuses };
}

// ---- the summary ------------------------------------------------------------------------------------------------------

export interface RiderDebtKpiView {
  readonly currency: string;
  readonly openMinor: bigint | undefined;
  readonly openDebts: number;
  readonly ridersInDebt: number | null;
  readonly partialDebts: number | null;
  readonly createdMinor: bigint | undefined;
  readonly recoveredMinor: bigint | undefined;
  readonly recoveredThisMonthMinor: bigint | undefined;
  /** The recovery rate as a percentage with one decimal ("11.7"), or null when nothing was owed in the period. */
  readonly recoveryPercent: string | null;
}

/** Basis points as a percentage with one decimal, without a float ("1169" -> "11.7"). */
export function percentOfBasisPoints(bp: number | null): string | null {
  if (bp === null || !Number.isFinite(bp)) return null;
  const tenths = Math.round(bp / 10);
  return `${Math.floor(tenths / 10)}.${tenths % 10}`;
}

export function riderDebtKpis(c: RiderDebtCurrencySummary): RiderDebtKpiView {
  const k = c.kpis;
  return {
    currency: c.currency,
    openMinor: minorOf(k.openMinor),
    openDebts: k.openDebts,
    ridersInDebt: k.ridersInDebt,
    partialDebts: k.partialDebts,
    createdMinor: minorOf(k.createdMinor),
    recoveredMinor: minorOf(k.recoveredMinor),
    recoveredThisMonthMinor: minorOf(k.recoveredThisMonthMinor),
    recoveryPercent: percentOfBasisPoints(k.recoveryBasisPoints),
  };
}

export interface RiderDebtChartsView {
  /** Recovered per month (columns), oldest first; `labels` are `YYYY-MM`. */
  readonly months: { readonly labels: readonly string[]; readonly recovered: readonly number[]; readonly created: readonly number[] };
  /** The age buckets as stacked columns: one series per origin. */
  readonly aging: { readonly buckets: readonly RiderDebtBucket[]; readonly series: readonly { readonly origin: string; readonly values: readonly number[] }[] };
  /** The open balance by origin (the donut). */
  readonly origins: readonly { readonly origin: string; readonly value: number }[];
}

/** Chart geometry takes numbers: a minor-unit amount beyond 2^53 cannot be drawn exactly, so it is clamped for the picture only (the text and the tooltip carry the exact string). */
const geometry = (raw: string): number => {
  const v = minorOf(raw);
  return v === undefined ? 0 : v > BigInt(Number.MAX_SAFE_INTEGER) ? Number.MAX_SAFE_INTEGER : Number(v);
};

export function riderDebtCharts(c: RiderDebtCurrencySummary): RiderDebtChartsView {
  const origins = [...new Set(c.aging.map((a) => a.origin))].sort();
  return {
    months: { labels: c.byMonth.map((m) => m.month), recovered: c.byMonth.map((m) => geometry(m.recoveredMinor)), created: c.byMonth.map((m) => geometry(m.createdMinor)) },
    aging: {
      buckets: RIDER_DEBT_BUCKET_LIST,
      series: origins.map((origin) => ({ origin, values: RIDER_DEBT_BUCKET_LIST.map((b) => c.aging.filter((a) => a.bucket === b && a.origin === origin).reduce((s, a) => s + geometry(a.openMinor), 0)) })),
    },
    origins: c.byOrigin.map((o) => ({ origin: o.origin, value: geometry(o.openMinor) })).filter((o) => o.value > 0),
  };
}

/** The currency block the screen shows: the chosen one, else the first. */
export function pickCurrency(summary: RiderDebtSummaryView, wanted: string): RiderDebtCurrencySummary | undefined {
  return summary.currencies.find((c) => c.currency === wanted) ?? summary.currencies[0];
}

// ---- the list -------------------------------------------------------------------------------------------------------

export type RiderDebtTone = 'neutral' | 'warning' | 'danger' | 'success';

export const bucketTone = (b: RiderDebtBucket | null): RiderDebtTone => (b === 'D61_PLUS' ? 'danger' : b === 'D31_60' ? 'warning' : 'neutral');
export const debtStatusTone = (s: RiderDebtStatus): RiderDebtTone => (s === 'SETTLED' ? 'success' : s === 'PARTIAL' ? 'warning' : 'danger');

export interface RiderDebtRowView {
  readonly key: string;
  readonly row: RiderDebtRiderRow;
  readonly openMinor: bigint | undefined;
  readonly amountMinor: bigint | undefined;
  readonly settledMinor: bigint | undefined;
}

export function riderDebtRows(rows: readonly RiderDebtRiderRow[]): readonly RiderDebtRowView[] {
  return rows.map((row) => ({ key: `${row.market}|${row.riderId}|${row.currency}`, row, openMinor: minorOf(row.openMinor), amountMinor: minorOf(row.amountMinor), settledMinor: minorOf(row.settledMinor) }));
}

export type RiderDebtScreenState = 'no_permission' | 'loading' | 'refused' | 'empty' | 'ready';

export function riderDebtScreenState(input: { readonly mayView: boolean; readonly error?: string; readonly loaded: boolean; readonly empty: boolean }): RiderDebtScreenState {
  if (!input.mayView) return 'no_permission';
  if (input.error !== undefined) return 'refused';
  if (!input.loaded) return 'loading';
  return input.empty ? 'empty' : 'ready';
}

// ---- the cash blocks and the release ---------------------------------------------------------------------------------------

export interface CashBlockRowView {
  readonly block: RiderCashBlockView;
  /** The release waiting for a checker, if any. */
  readonly pending: RiderCashReleaseView | null;
  /** The operator may decide it: has the approve word and is not the proposer. */
  readonly mayDecide: boolean;
  /** The operator holds the approve word but proposed it: the console says why the decision is not offered. */
  readonly ownProposal: boolean;
  /** The operator may propose one: has the propose word and none is pending. */
  readonly mayPropose: boolean;
}

/** D868: `authority.selfApproval` (a GLOBAL super administrator) decides their own release proposal too. */
export function cashBlockRows(view: RiderCashBlocksView, operatorId: string | undefined, authority: { readonly mayPropose: boolean; readonly mayApprove: boolean; readonly selfApproval?: boolean }): readonly CashBlockRowView[] {
  return view.blocked.map((block) => {
    const pending = block.pendingRelease;
    const own = pending !== null && operatorId !== undefined && pending.proposedBy === operatorId && authority.selfApproval !== true;
    return {
      block,
      pending,
      ownProposal: own && authority.mayApprove,
      mayDecide: pending !== null && authority.mayApprove && !own,
      mayPropose: pending === null && authority.mayPropose,
    };
  });
}

export const releaseTone = (s: RiderCashReleaseView['status']): RiderDebtTone => (s === 'APPROVED' ? 'success' : s === 'REJECTED' ? 'danger' : 'warning');

/** A reason code the console offers, else any stable code the server holds (history shows what was recorded). */
export const isOfferedReason = (code: string): code is CashReleaseReason => (CASH_RELEASE_REASONS as readonly string[]).includes(code);

// ---- the notice ------------------------------------------------------------------------------------------------------------

/** The render parameters of a `RIDER_DEBT_SETTLED` alert: an amount and a currency, a debt reference, how many rides paid it - never a person. */
export function debtAlertParams(params: Readonly<Record<string, string | number | boolean>>): { readonly amountMinor: bigint | undefined; readonly currency: string | undefined; readonly debtRef: string | undefined; readonly origin: string | undefined; readonly payments: number | undefined } {
  const amount = params.amountMinor;
  return {
    amountMinor: typeof amount === 'string' ? minorOf(amount) : typeof amount === 'number' && Number.isInteger(amount) && amount >= 0 ? BigInt(amount) : undefined,
    currency: typeof params.currency === 'string' ? params.currency : undefined,
    debtRef: typeof params.debtRef === 'string' ? params.debtRef : undefined,
    origin: typeof params.origin === 'string' ? params.origin : undefined,
    payments: typeof params.payments === 'number' ? params.payments : undefined,
  };
}
